import { APP_API_URL, IS_BIKELEASING, PAPI_V0_URL } from '@constants';
import useAuth from '@contexts/AuthProvider';
import axios from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';

const MAX_POLLS = 15;

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export function useConfirmRegistration() {
  const { token } = useAuth();
  const [isConfirming, setIsConfirming] = useState(false);
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    return () => {
      cancelledRef.current = true;
    };
  }, []);

  // Returns true once the registration is visible in the authoritative backend.
  const buildProbe = useCallback(
    (sub: string | undefined): (() => Promise<boolean>) => {
      if (IS_BIKELEASING) {
        return async () => {
          if (!sub) return true;
          const res = await axios.get<{ result: boolean }>(`${PAPI_V0_URL}/employees/exists`, { params: { sub } });
          return res.data.result === true;
        };
      }
      return async () => {
        await axios.get(`${APP_API_URL}/me`, { headers: { Authorization: `Bearer ${token}` } });
        return true;
      };
    },
    [token],
  );

  const confirm = useCallback(
    async (sub: string | undefined) => {
      setIsConfirming(true);
      const probe = buildProbe(sub);
      try {
        for (let attempt = 1; attempt <= MAX_POLLS; attempt++) {
          await delay(1000 + (attempt - 1) * 250);
          if (cancelledRef.current) return;
          try {
            if (await probe()) return;
          } catch {
            // transient failure — keep polling until MAX_POLLS, then fall through
          }
        }
      } finally {
        if (!cancelledRef.current) setIsConfirming(false);
      }
    },
    [buildProbe],
  );

  return { confirm, isConfirming };
}
